import { EmailTemplate, EmailTemplateWithEventName, DirectUploadFile } from "@/types/EmailTemplate";
import { apiPost, apiGet, apiDelete, apiPut } from "../_apiShim";
import {
  builderIfMatchFrom,
  builderReadTicket,
  builderVersionFor,
  rememberBuilderVersion,
  settleBuilderVersion,
} from "./_builderVersionStore";

const BASE_PATH = "/api/v1/email-template/";
const TEMPLATE_BASE_PATH = "/api/v1/template/email-template/";

export async function createEmailTemplate(
  emailTemplate: EmailTemplate
): Promise<string> {
  // F36 follow-up: eventId moved to the path so the backend plugin filter
  // can resolve it. The body still carries the full EmailTemplate shape;
  // eventId field is redundant but harmless.
  return await apiPost<string>(
    `${BASE_PATH}${emailTemplate.eventId}`,
    emailTemplate,
    {
      "Cache-Control": "no-store",
    }
  );
}

export async function getEmailTemplate(
  eventId: string,
  emailTemplateId: string,
  fields: string
): Promise<EmailTemplate> {
  // W2-F (M19): always co-fetch the optimistic-concurrency version so the
  // save PUT can echo it as If-Match (stale two-tab writes then 409).
  const url = `${BASE_PATH}${eventId}/${emailTemplateId}?fields=${encodeURIComponent(
    fields ? `${fields},version` : "version"
  )}`;

  const ticket = builderReadTicket(emailTemplateId);
  const template = await apiGet<EmailTemplate>(
    url,
    {
      "Cache-Control": "no-store",
    },
    true
  );
  rememberBuilderVersion(emailTemplateId, (template as { version?: number })?.version, ticket);
  return template;
}

export async function getEmailTemplatesByEvent(
  eventId: string,
  fields: string
): Promise<EmailTemplate[]> {
  const url = `${BASE_PATH}event/${eventId}?fields=${encodeURIComponent(
    fields
  )}`;

  // No catch-return-[]: swallowing here made a list 5xx render the
  // "No email templates found" empty state before the handler's
  // ServerError mapping (and the page's SError branch) could run
  // (BUGS.md H11, FIXED 2026-07-05 W2-E).
  return await apiGet<EmailTemplate[]>(
    url,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

export async function getEmailTemplatesByUserPaginated(
  excludeEventId: string,
  fields: string,
  page: number,
  pageSize: number,
  sortBy: string,
  sortOrder: string,
  search: string = ""
): Promise<EmailTemplateWithEventName[]> {
  let url = `${BASE_PATH}user/paginated?excludeEventId=${encodeURIComponent(
    excludeEventId
  )}&fields=${encodeURIComponent(fields)}&page=${page}&pageSize=${pageSize}&sortBy=${encodeURIComponent(
    sortBy
  )}&sortOrder=${encodeURIComponent(sortOrder)}`;

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  try {
    return await apiGet<EmailTemplateWithEventName[]>(
      url,
      {
        "Cache-Control": "no-store",
      },
      true
    );
  } catch {
    return [];
  }
}

// The event-scoped route: the event role decides (`email-templates:delete`),
// not the caller's home-company role — the id-only twin is OWNER/ADMIN of the
// LOGIN company and refused every same-company EDITOR and every outside
// collaborator (event-scope wave).
export async function deleteEmailTemplate(
  eventId: string,
  emailTemplateId: string
): Promise<void> {
  await apiDelete<string>(`${BASE_PATH}${eventId}/${emailTemplateId}`, {
    "Cache-Control": "no-store",
  });
}

export async function updateEmailTemplate(
  eventId: string,
  emailTemplateId: string,
  emailTemplate: Partial<EmailTemplate>
): Promise<void> {
  const url = `${BASE_PATH}${eventId}/${emailTemplateId}`;
  const sent = builderVersionFor(emailTemplateId);

  await apiPut<void>(url, emailTemplate, {
    "Cache-Control": "no-store",
    // W2-F (M19): echo the loaded version — backend 409s stale writes.
    ...builderIfMatchFrom(sent),
  });
  settleBuilderVersion(emailTemplateId, sent);
}

export async function bulkDuplicateEmailTemplates(
  eventId: string,
  emailTemplateIds: string[],
): Promise<EmailTemplate[]> {
  return await apiPost<EmailTemplate[]>(
    `${BASE_PATH}${eventId}/bulk-duplicate`,
    { emailTemplateIds },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function presignDirectUpload(
  eventId: string,
  emailTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number }
): Promise<{ uploadUrl: string; s3Key: string; fileId: string }> {
  const url = `${BASE_PATH}${eventId}/${emailTemplateId}/files/presign`;
  return await apiPost<{ uploadUrl: string; s3Key: string; fileId: string }>(url, data, {
    "Cache-Control": "no-store",
  });
}

export async function confirmDirectUpload(
  eventId: string,
  emailTemplateId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string }
): Promise<DirectUploadFile> {
  const url = `${BASE_PATH}${eventId}/${emailTemplateId}/files/confirm`;
  return await apiPost<DirectUploadFile>(url, data, {
    "Cache-Control": "no-store",
  });
}

export async function deleteDirectUpload(
  eventId: string,
  emailTemplateId: string,
  fileId: string
): Promise<void> {
  const url = `${BASE_PATH}${eventId}/${emailTemplateId}/files/${fileId}`;
  await apiDelete<void>(url, {
    "Cache-Control": "no-store",
  });
}

// ── Template email template endpoints ─────────────────────────────────────────

export async function createTemplateEmailTemplate(
  templateId: string,
  emailTemplate: EmailTemplate
): Promise<string> {
  return await apiPost<string>(
    `${TEMPLATE_BASE_PATH}${templateId}`,
    emailTemplate,
    {
      "Cache-Control": "no-store",
    }
  );
}

export async function getTemplateEmailTemplates(
  templateId: string,
  fields: string
): Promise<EmailTemplate[]> {
  const url = `${TEMPLATE_BASE_PATH}${templateId}?fields=${encodeURIComponent(fields)}`;
  // No catch-return-[] — see getEmailTemplatesByEvent (BUGS.md M4/M12,
  // FIXED 2026-07-05 W2-E). The handler wrapper maps failures to
  // ServerError so pages can surface them.
  return await apiGet<EmailTemplate[]>(
    url,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

export async function getTemplateEmailTemplate(
  templateId: string,
  emailTemplateId: string,
  fields: string
): Promise<EmailTemplate> {
  // W2-F (M19): see getEmailTemplate — co-fetch + remember the version.
  const url = `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}?fields=${encodeURIComponent(fields ? `${fields},version` : "version")}`;
  const ticket = builderReadTicket(emailTemplateId);
  const template = await apiGet<EmailTemplate>(
    url,
    {
      "Cache-Control": "no-store",
    },
    true
  );
  rememberBuilderVersion(emailTemplateId, (template as { version?: number })?.version, ticket);
  return template;
}

export async function updateTemplateEmailTemplate(
  templateId: string,
  emailTemplateId: string,
  emailTemplate: Partial<EmailTemplate>
): Promise<void> {
  const sent = builderVersionFor(emailTemplateId);
  await apiPut<void>(
    `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}`,
    emailTemplate,
    {
      "Cache-Control": "no-store",
      // W2-F (M19): echo the loaded version — backend 409s stale writes.
      ...builderIfMatchFrom(sent),
    },
  );
  settleBuilderVersion(emailTemplateId, sent);
}

export async function presignTemplateDirectUpload(
  templateId: string,
  emailTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number }
): Promise<{ uploadUrl: string; s3Key: string; fileId: string }> {
  const url = `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}/files/presign`;
  return await apiPost<{ uploadUrl: string; s3Key: string; fileId: string }>(url, data, {
    "Cache-Control": "no-store",
  });
}

export async function confirmTemplateDirectUpload(
  templateId: string,
  emailTemplateId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string }
): Promise<DirectUploadFile> {
  const url = `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}/files/confirm`;
  return await apiPost<DirectUploadFile>(url, data, {
    "Cache-Control": "no-store",
  });
}

export async function deleteTemplateDirectUpload(
  templateId: string,
  emailTemplateId: string,
  fileId: string
): Promise<void> {
  const url = `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}/files/${fileId}`;
  await apiDelete<void>(url, {
    "Cache-Control": "no-store",
  });
}

export async function deleteTemplateEmailTemplate(
  templateId: string,
  emailTemplateId: string
): Promise<void> {
  await apiDelete<string>(
    `${TEMPLATE_BASE_PATH}${templateId}/${emailTemplateId}`,
    {
      "Cache-Control": "no-store",
    }
  );
}
